import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { Button, Alert, AlertDescription, useAuth } from '@aaas/shared';
import { useSpaceDetailQuery } from '../hooks/useSpaceListQuery';
import ChatInterface from '../components/ChatInterface';

const SpaceChatPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();

  // React Query for space details
  const {
    data: space,
    isLoading: loading,
    error,
  } = useSpaceDetailQuery({
    spaceId: id,
    idToken: idToken || undefined,
    enabled: !!id && !!user && !!currentWorkspace,
  });

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load space'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="w-full p-6">
        <Alert>
          <AlertDescription>Space not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full p-6 space-y-6">
      {/* Header */}
      <div className="flex gap-4">
        {/* Arrow container */}
        <div className="pt-1">
          <Button
            variant="ghost"
            onClick={() => navigate(`/kms/spaces/${space.id}`)}
            className="p-2"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </div>

        {/* Main + Sub header container */}
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-6 h-6 text-green-600" />
            <h1 className="text-3xl font-bold">Chat with Documents</h1>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="font-medium text-gray-600">{space.name}</span>
          </div>
        </div>
      </div>

      {/* Chat Interface */}
      <ChatInterface spaceId={space.id} spaceName={space.name} />
    </div>
  );
};

export default SpaceChatPage;
